import { useIsFocused, useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  AppState,
  AppStateStatus,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl,
  ViewToken,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToast } from 'react-native-toast-notifications';

import EmptyStateNoSearch from '@components/EmptyState/EmptyStateNoSearch';
import { HeroCarousel } from '@components/MyTasks/HeroCarousel';
import { MyTaskGroupCard } from '@components/MyTasks/MyTaskGroupCard';
import {
  buildAssigneeOptions,
  buildProjectOptions,
} from '@components/MyTasks/myTasksFilterOptions';
import {
  MyTaskGroup,
  MyTasksGroupBy,
  MyTasksOrderBy,
  MyTasksTab,
  ProjectGroupInput,
  TASK_GROUP_BY_FOR,
  TASK_SORT_BY_FOR,
  applyPinnedPositions,
  applyStickyOrder,
  composeServerGroups,
  composeUnscheduledGroup,
  computeMyTasksDateWindows,
  excludeEmptyGroups,
  excludeFullyCompleteGroups,
  groupCountLabel,
  groupSectionLabel,
  mergeServerGroups,
  pinUnassignedFirst,
  retainTouchedGroups,
  selectHeroTasks,
  sortFullyCompleteGroupsLast,
  canSortGroupTasksOnClient,
  sortGroupTasks,
  withoutCompletedTasks,
} from '@components/MyTasks/myTasksGrouping';
import { MyTasksTabBar } from '@components/MyTasks/MyTasksTabBar';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { FloatingActionButton } from '@components/shared/Button/FloatingActionButton';
import {
  MyTaskGroupCardSkeleton,
  SkeletonList,
} from '@components/shared/skeletons';
import {
  Task,
  TaskGroup,
  TaskPriority,
  TaskSortOption,
  TaskStatus,
  useCompleteTaskMutation,
} from '@graphql/generated';
import { useListMyTasksProjectGroupsFromQuery } from '@hooks/useListMyTasksProjectGroupsFromQuery';
import { useListTasksFromQuery } from '@hooks/useListTasksFromQuery';
import { useListUnscheduledTasksFromQuery } from '@hooks/useListUnscheduledTasksFromQuery';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useRefetchOnFocus } from '@hooks/useRefetchOnFocus';
import { AppStackScreenProps } from '@navigation/app-stack';
import { getDateRangeForLabel, QuickDueLabel } from '@screens/Tasks/Tasks.web';
import {
  MIXPANEL_EVENTS,
  MY_TASKS_SAFETY_POLL_INTERVAL_MS,
} from '@src/constants/constants';
import theme from '@themes/theme';
import { logError } from '@utils/logNonFatalError';
import { useMixpanel } from '@utils/mixpanel';
import { refetchActiveQueriesFresh } from '@utils/refetchActiveQueriesFresh';
import { completeTaskFailureNeedsMessage } from '@utils/tasks/completeTaskFailure';
import {
  isCompletionConfirmedInCache,
  readLiveCompletedAt,
  MAX_RELEASE_ATTEMPTS,
  UNCONFIRMED_RETRY_MS,
} from '@utils/tasks/completionConfirm';

import type { ProjectUpdatedRefreshMode } from '@hooks/useRefreshOnProjectUpdated';

/** Project grouping prefetch: fetch the next page once the group this many
 * positions from the end scrolls into view. */
const PROJECT_GROUPS_LOAD_MORE_OFFSET = 2;

/** Unscheduled card's own page size — larger than TASK_PAGE_SIZE (used
 * elsewhere for the default flat listTasks page) since Unscheduled rows are
 * plain, unvirtualized Boxes (MyTaskGroupCard), not FlatList items; each
 * "Load More" press mounts this many more rows at once. */
const UNSCHEDULED_PAGE_SIZE = 50;

/** Rounded top corners of My Day's white content sheet in its default (Hero
 * still on screen) state; it animates to 0 once the sheet docks under the tab
 * bar. `theme.borderRadii.m` is 16. */
const SHEET_TOP_RADIUS = theme.borderRadii.m;
/** How long the sheet's corners take to round/flatten as it docks/undocks. */
const SHEET_RADIUS_DURATION_MS = 150;

/**
 * Whether My Day's white content sheet has scrolled up far enough to sit flush
 * against the tab bar, at which point its rounded top corners flatten.
 *
 * `heroTranslateY` clamps the Hero's collapse at `heroHeight`, so that offset
 * is exactly where the sheet docks. A non-positive `heroHeight` means the Hero
 * hasn't been measured yet (or there is no Hero at all) — reporting "docked"
 * there would flatten the corners underneath a Hero that is about to appear,
 * so it counts as undocked regardless of offset.
 */
export const isSheetDockedAtOffset = (
  offsetY: number,
  heroHeight: number
): boolean => heroHeight > 0 && offsetY >= heroHeight;

/** Flattens open + completed tasks out of a set of server-side task groups —
 * used to feed the Hero carousel and the filter picker's project/assignee
 * options from whichever grouped query is currently active. Deduped by id:
 * grouping by Assignee puts a multi-assignee task in more than one group,
 * so concatenating every group's tasks verbatim can return the same task
 * twice — that duplicate id is what surfaced as HeroCarousel's dots
 * ("duplicate key") warning, one level up from the per-group-node dedupe
 * already in useListMyTasksProjectGroupsFromQuery's flattenTasks. */
const flattenGroupTasks = (groups: ProjectGroupInput[]): Task[] => {
  const seenIds = new Set<string>();
  const tasks: Task[] = [];
  groups.forEach((group) => {
    [...group.tasks, ...group.completedTasks].forEach((task) => {
      if (seenIds.has(task.id)) return;
      seenIds.add(task.id);
      tasks.push(task);
    });
  });
  return tasks;
};

const renderMyTaskGroupCardSkeletonRow = (index: number) => (
  <MyTaskGroupCardSkeleton key={index} />
);

/** Minimal shape shared by every filter-item type (TasksFilterProject,
 * TasksFilterUser, PriorityFilterUser, DueDateFilter, ProjectTask* variants,
 * …) — this view only ever reads `.value` off them, so it accepts either
 * useTasksFilter's or useProjectTaskFilter's items without depending on
 * either context. */
type FilterValue<T> = { value: T };

export type TasksListViewProps = {
  /** Scopes every query to this one project (Project Tasks) instead of the
   * current user's tasks across all their projects (My Tasks, when
   * undefined). Also forces the filter sheet's Projects row/chip hidden and
   * the per-row project name/avatar hidden — grouping and showing a task's
   * own project are both meaningless once already scoped to one project. */
  projectId?: string;
  /** true for My Tasks (tasks the viewer is part of), false for Project
   * Tasks (every task in the project regardless of the viewer's own
   * involvement). */
  taskCollaborators: boolean;
  /** Which filter context MyTasksFilterSheet reads/writes — it branches on
   * this itself, so this view never needs to know the context's shape. */
  filterScope: 'myTasks' | 'projectTasks';
  /** Compared against groupByOption/orderByOption for the tab bar's "active
   * filter" dot — My Tasks defaults to Project, Project Tasks to Assignee. */
  defaultGroupByOption: MyTasksGroupBy;

  groupByOption: MyTasksGroupBy;
  orderByOption: MyTasksOrderBy;
  /** My Tasks only — Project Tasks has no per-task-screen project filter,
   * its scope is fixed by `projectId` instead. */
  projectFilters?: FilterValue<string>[];
  userFilters: FilterValue<string>[];
  priorityFilters: FilterValue<TaskPriority>[];
  dueDateFilters: FilterValue<string>[];
  dueDateStrFilters?: FilterValue<{ start?: any; end?: any } | undefined>;
  /** Called once on mount to clear filters left over from a previous visit
   * (or from the other screen, if filter state happened to be shared —
   * it isn't, but this keeps that guarantee explicit either way). */
  resetFilters: () => void;

  /** Extra side effect once a task is actually marked complete (not
   * reopened) — e.g. Project Tasks' rate-app prompt trigger. */
  onTaskCompleted?: () => void;
  /** Fires whenever the set of currently-loaded tasks changes (same set
   * used for the filter picker's project/assignee options) — e.g. Project
   * Tasks' member-task-nudge effect, which has no query state of its own. */
  onTasksLoaded?: (tasks: Task[]) => void;

  fabLabel: string;
  onCreateTaskPress: () => void;
  fabDisabled?: boolean;
  hideFab?: boolean;
  /** Set on screens rendered outside the bottom-tab navigator, where nothing
   * else covers the bottom inset and the FAB would otherwise sit on the
   * Android gesture bar. My Tasks lives inside the tab navigator, whose tab
   * bar already fills that strip — adding the inset there would just float
   * the FAB too high. */
  fabRespectsBottomInset?: boolean;

  /** My Tasks only — MainHeader's popup filter/sort action re-dispatched as
   * a route param; ProjectTasksScreen has no equivalent header action. */
  headerEvent?: { type: 'doSortTaskList' | 'doFilterTaskList'; ts: number };
};

/**
 * Shared list/data view for the My Tasks revamp (TA-10186/10187) and its
 * project-scoped variant (TA-10275, TA-10446 follow-up): 3 fixed tabs (My
 * Day / 7 Days / All Tasks), overdue hero carousel on My Day, group cards
 * with default Overdue → High → Medium → Low sorting, an Unscheduled card on
 * All Tasks, and the filter bottom sheet driving group-by/order-by/project/
 * assignee/priority/due-date across all tabs. Both MyTasksScreen and
 * ProjectTasksScreen render this — the only real differences between them
 * are query scope (`projectId`/`taskCollaborators`) and which filter context
 * feeds it, both passed in as props so this component stays agnostic to
 * either. Onboarding/nudge modals and the FAB's own permission gating are
 * each screen's own concern, layered outside this view.
 */
export const TasksListView: React.FC<TasksListViewProps> = ({
  projectId,
  taskCollaborators,
  filterScope,
  defaultGroupByOption,
  groupByOption,
  orderByOption,
  projectFilters = [],
  userFilters,
  priorityFilters,
  dueDateFilters,
  dueDateStrFilters,
  resetFilters,
  onTaskCompleted,
  onTasksLoaded,
  fabLabel,
  onCreateTaskPress,
  fabDisabled = false,
  hideFab = false,
  fabRespectsBottomInset = false,
  headerEvent,
}) => {
  const navigation =
    useNavigation<AppStackScreenProps<'tasks-stack'>['navigation']>();
  const insets = useSafeAreaInsets();
  const toast = useToast();
  const { t: tShared } = useTranslation('shared');
  const { trackEvent } = useMixpanel();
  const hideProjectsFilter = !!projectId;
  const hideProjectName = !!projectId;

  // TA-10460 follow-up: a task assigned from another session/device while
  // this screen is already open stayed invisible until the next focus event
  // (or a full app restart). TA-11139: My Tasks refreshes when a
  // projectUpdated payload arrives (the backend publishes it to every project
  // member on task create/complete/update/delete/assign), with a slow
  // safety-net poll for changes that publish none. My Tasks only (projectId
  // undefined — Project Tasks' task set isn't scoped to the viewer's own
  // assignments), and only while this tab is actually focused and the app is
  // in the foreground, so it doesn't burn network/battery in the background
  // or while the user is on a different tab. An event that lands meanwhile
  // is refreshed once on the way back.
  const isFocused = useIsFocused();
  // AppState.currentState isn't reliably 'active' the instant this component
  // mounts (native module readiness on cold start, and the RN test
  // environment's AppState mock doesn't seed it to 'active' at all) —
  // defaulting to true and letting the listener below correct it if the app
  // is genuinely backgrounded is safer than defaulting polling off, since
  // the cost of guessing wrong is one extra poll cycle, not a broken screen.
  const [isAppActive, setIsAppActive] = useState(true);
  useEffect(() => {
    const handleAppStateChange = (nextState: AppStateStatus) =>
      setIsAppActive(nextState === 'active');
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => subscription.remove();
  }, []);
  const isMyTasksLive = !projectId && isFocused && isAppActive;
  const myTasksPollIntervalMs = isMyTasksLive
    ? MY_TASKS_SAFETY_POLL_INTERVAL_MS
    : undefined;
  // In the background: catch up once on return, since returning to the app
  // is not a focus event and the socket may have missed events meanwhile.
  // Unfocused: the tab's own queries ignore the events, because coming back
  // already refetches them (useRefetchOnFocus below) and a held event would
  // only add a second request. The hero is not part of that refetch, so it
  // holds the events instead (Copilot review).
  let heroRefreshMode: ProjectUpdatedRefreshMode | undefined;
  let myTasksRefreshMode: ProjectUpdatedRefreshMode | undefined;
  if (!projectId) {
    if (!isAppActive) heroRefreshMode = 'background';
    else heroRefreshMode = isFocused ? 'live' : 'paused';
    if (isFocused) myTasksRefreshMode = heroRefreshMode;
  }

  const [activeTab, setActiveTab] = useState<MyTasksTab>(MyTasksTab.MyDay);
  // Switching tabs (especially onto All Tasks, the heaviest one) un-skips
  // Apollo queries and recomputes several useMemos in the same commit as the
  // tab press — synchronous enough to occasionally stall the JS thread long
  // enough that TouchableOpacity's native-driven press-release animation
  // (queued as a JS event) visibly gets stuck at activeOpacity, flashing the
  // just-tapped tab grey/faded until the thread frees up. `activeTab` itself
  // stays synchronous so MyTasksTabBar's highlight swaps instantly on tap;
  // every other (expensive) consumer below reads this deferred copy instead,
  // so React can deprioritize that work behind the press-feedback event.
  const deferredActiveTab = useDeferredValue(activeTab);

  // This view only mounts on a fresh app launch or after a (re-)login for My
  // Tasks — React Navigation keeps it mounted across tab switches otherwise
  // — and on every visit for Project Tasks (a stack screen) — so a
  // mount-only reset covers both without clobbering filters set earlier in
  // the same visit. resetFilters isn't stable across renders, so it's read
  // from a ref instead of the effect's deps — adding it there would turn
  // this into a reset-on-every-render instead of mount-only.
  const resetFiltersRef = useRef(resetFilters);
  resetFiltersRef.current = resetFilters;
  useEffect(() => {
    resetFiltersRef.current();
  }, []);

  const filterProjectIds = useMemo(
    () => projectFilters.map((p) => p.value),
    [projectFilters]
  );
  const filterAssigneeIds = useMemo(
    () => userFilters.map((u) => u.value),
    [userFilters]
  );

  const dueFilterRange = useMemo(() => {
    if (dueDateFilters.length > 0) {
      const range = getDateRangeForLabel(
        dueDateFilters[0].value as QuickDueLabel
      );
      if (range) {
        return {
          begin: dayjs(range.start).startOf('day'),
          end: dayjs(range.end).endOf('day'),
        };
      }
    }
    if (dueDateStrFilters?.value?.start) {
      return {
        begin: dayjs(dueDateStrFilters.value.start).startOf('day'),
        end: dayjs(
          dueDateStrFilters.value.end ?? dueDateStrFilters.value.start
        ).endOf('day'),
      };
    }
    return null;
  }, [dueDateFilters, dueDateStrFilters]);

  const dateWindows = useMemo(
    () => computeMyTasksDateWindows(dueFilterRange),
    [dueFilterRange]
  );

  const isMyDayServerGrouped = deferredActiveTab === MyTasksTab.MyDay;
  const isSevenDaysServerGrouped = deferredActiveTab === MyTasksTab.SevenDays;
  const isAllTasksServerGrouped = deferredActiveTab === MyTasksTab.AllTasks;
  const serverGroupBy = TASK_GROUP_BY_FOR[groupByOption];
  // Selecting Order By must hit the backend (not just re-sort what's already
  // loaded) so paginated groups/tasks come back in the right order too.
  const serverSortBy = TASK_SORT_BY_FOR[orderByOption];

  // projectId (when set) always wins over the filter sheet's own project
  // scope — Project Tasks hides the Projects row precisely because it must
  // never be user-widenable from here.
  let scopedProjectIds: string[] | undefined;
  if (projectId) {
    scopedProjectIds = [projectId];
  } else if (filterProjectIds.length > 0) {
    scopedProjectIds = filterProjectIds;
  }
  const projectGroupsFilterVars = {
    projectIds: scopedProjectIds,
    memberIds: filterAssigneeIds.length > 0 ? filterAssigneeIds : undefined,
    priority:
      priorityFilters.length > 0
        ? priorityFilters.map((p) => p.value)
        : undefined,
    taskCollaborators,
    groupBy: serverGroupBy,
    sortBy: serverSortBy,
  };

  // Identity of the list every query below is about to fetch. Two uses, both
  // needing it before `groups` is composed: the refetch-on-filter-change
  // effect further down, and the in-flight row-order pin (see
  // stabilizeTaskOrder), which must not carry a snapshot across a change in
  // what the list even contains.
  const filterSignature = JSON.stringify({
    groupByOption,
    orderByOption,
    ...projectGroupsFilterVars,
    dueDateBegin: dateWindows.filterBegin,
    dueDateEnd: dateWindows.filterEnd,
  });

  // My Day has no single due_group covering "overdue + today", so this
  // fetches Overdue and Current(capped at today) task groups separately and
  // merges them below via mergeServerGroups. These two also feed the Hero
  // carousel (Overdue) and, combined with the other grouped queries below,
  // the filter picker's project/assignee options — this screen no longer
  // runs any flat listTasks query of its own except for Unscheduled.
  const myDayOverdueProjectGroupsQuery = useListMyTasksProjectGroupsFromQuery({
    ...projectGroupsFilterVars,
    dueGroup: TaskGroup.Overdue,
    // TA-10495 #11: unlike heroOverdueTasksQuery below (which deliberately
    // ignores a future-only due-date filter so the Hero carousel keeps
    // showing every overdue task), this is the actual visible My Day list —
    // it must respect the active Due Date filter like every other bucket.
    // Selecting "Tomorrow" correctly yields zero overdue groups here, since
    // no overdue task can be due tomorrow.
    dueDateBegin: dateWindows.filterBegin,
    dueDateEnd: dateWindows.filterEnd,
    skip: !isMyDayServerGrouped,
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: myTasksRefreshMode,
    // Same reason as weekProjectGroupsQuery below — see the fetchPolicy note
    // there. `skip` puts this query in Apollo's `standby` state whenever My
    // Day isn't the active tab, and standby queries are excluded from
    // name-based `refetchQueries`, so a task edit made elsewhere (e.g. the
    // priority modal, which refetches `listMyTasksProjectGroups`) never
    // reaches it. cache-first then served the pre-edit group edges on the
    // next visit, leaving the task under its old priority/assignee card.
    fetchPolicy: 'cache-and-network',
  });
  const myDayTodayProjectGroupsQuery = useListMyTasksProjectGroupsFromQuery({
    ...projectGroupsFilterVars,
    dueGroup: TaskGroup.Current,
    dueDateBegin: dateWindows.filterBegin,
    dueDateEnd: dateWindows.todayEnd,
    skip: !isMyDayServerGrouped,
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: myTasksRefreshMode,
    fetchPolicy: 'cache-and-network',
  });

  // Hero (AC12/13) only ever needs a flat, soonest-first list of overdue
  // tasks — it has no stake in groupBy's dimension/shape. Deriving it from
  // myDayOverdueProjectGroupsQuery.groups instead ties Hero to that query's
  // groupBy-shaped previousData guard (see useListMyTasksProjectGroupsFromQuery):
  // the filter sheet commits Group By alongside whatever filter the user
  // actually meant to change on every Apply (and Reset reverts it to the
  // default), so a groupBy value change discards previousData to avoid a
  // duplicate-key crash — flashing Hero empty on any Apply/Reset where Group
  // By happened to differ from its previous value, not just ones where the
  // user touched a due-date filter. A dedicated flat query with no groupBy
  // variable at all sidesteps that entire class of flash.
  const heroOverdueTasksQuery = useListTasksFromQuery({
    dueGroup: TaskGroup.Overdue,
    excludeCompleted: true,
    sortBy: TaskSortOption.OverdueLow,
    projectIds: projectGroupsFilterVars.projectIds,
    memberIds: projectGroupsFilterVars.memberIds,
    priority: projectGroupsFilterVars.priority,
    taskCollaborators,
    dueDateBegin: dateWindows.overdueBegin,
    dueDateEnd: dateWindows.overdueEnd,
    skip: !isMyDayServerGrouped,
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: heroRefreshMode,
  });

  // 7 Days shows no completed items (AC3), so excludeCompleted keeps each
  // group's completedTasks connection empty rather than filtering client-side.
  const weekProjectGroupsQuery = useListMyTasksProjectGroupsFromQuery({
    ...projectGroupsFilterVars,
    excludeCompleted: true,
    dueDateBegin: dateWindows.weekBegin,
    dueDateEnd: dateWindows.weekEnd,
    // An empty intersection between the 7-day window and the due-date filter
    // means "no task can qualify" — skip rather than query a backwards range.
    skip: !isSevenDaysServerGrouped || dateWindows.weekRangeIsEmpty,
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: myTasksRefreshMode,
    // TA-10495 #4: this query's own `skip` is the only thing gating its
    // network activity, and it's re-toggled on every tab switch (not screen
    // focus) — with the default cache-first policy, revisiting a tab whose
    // variables were already cached served stale data with no network round
    // trip, so a task whose date moved into the current week didn't show up
    // until the user forced a refetch via applying/resetting a filter.
    // cache-and-network shows the cached groups instantly (no empty flash)
    // but still refetches every time the tab becomes active again.
    fetchPolicy: 'cache-and-network',
  });

  // Unscheduled card (AC4): fetched directly via withoutDueDate rather than
  // split out client-side from the grouped query — listMyTasksProjectGroups
  // has no equivalent filter (only requireDueDate, which can't isolate *only*
  // undated tasks) and its pagination is grouped by dimension, not a flat
  // independently-paginated list, so this is the one query on the screen that
  // still goes through a flat listTasks-shaped query. Runs regardless of
  // groupByOption — the Unscheduled card is independent of Group By. Uses
  // the lightweight listTasksLight (MyTasksGroupTask fragment) rather than
  // full listTasks/TaskDetail — MyTaskGroupCard's row only ever reads the
  // same handful of fields already used by the server-grouped tabs.
  // Unscheduled tasks have no due date by definition — once the filter
  // sheet's due-date filter is active, none of them can ever match it, so
  // the card is hidden entirely (rather than showing a permanently-empty
  // "Unscheduled" section) instead of running a query for it.
  // Deliberately does NOT pass excludeCompleted (TA-10487): the grouped
  // queries default to requireDueDate: true, so an undated task exists on
  // mobile only inside this card. Excluding completed ones here made a task
  // vanish from every tab of both My Tasks and Project Tasks the moment it
  // was checked off, with no surface left to find it on. They come back in
  // the same connection and composeUnscheduledGroup splits them into the
  // card's collapsed "Show completed" section below.
  const allTasksUnscheduledQuery = useListUnscheduledTasksFromQuery({
    withoutDueDate: true,
    taskCollaborators,
    dueDateBegin: dateWindows.filterBegin,
    dueDateEnd: dateWindows.filterEnd,
    sortBy: serverSortBy,
    first: UNSCHEDULED_PAGE_SIZE,
    skip: deferredActiveTab !== MyTasksTab.AllTasks || dueFilterRange !== null,
    projectIds: projectGroupsFilterVars.projectIds,
    memberIds: projectGroupsFilterVars.memberIds,
    priority: projectGroupsFilterVars.priority,
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: myTasksRefreshMode,
  });

  // Grouping (AC8): server groups tasks by the selected dimension directly
  // instead of the client bucketing a flat listTasks result.
  const allTasksProjectGroupsQuery = useListMyTasksProjectGroupsFromQuery({
    ...projectGroupsFilterVars,
    dueDateBegin: dateWindows.filterBegin,
    dueDateEnd: dateWindows.filterEnd,
    skip: !isAllTasksServerGrouped,
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: myTasksRefreshMode,
    // See myDayOverdueProjectGroupsQuery / weekProjectGroupsQuery above.
    fetchPolicy: 'cache-and-network',
  });

  // Picker options come from the tasks already loaded for the screen — no
  // extra query. Only whichever grouped query the active tab fetches
  // actually has data (the others are skipped), so options widen as the
  // user visits more tabs/dimensions rather than being available up-front;
  // knownProjectsRef/knownAssigneesRef below accumulate across that.
  const allLoadedTasks = useMemo(
    () => [
      ...flattenGroupTasks(myDayOverdueProjectGroupsQuery.groups),
      ...flattenGroupTasks(myDayTodayProjectGroupsQuery.groups),
      ...flattenGroupTasks(weekProjectGroupsQuery.groups),
      ...flattenGroupTasks(allTasksProjectGroupsQuery.groups),
      ...allTasksUnscheduledQuery.tasks,
    ],
    [
      myDayOverdueProjectGroupsQuery.groups,
      myDayTodayProjectGroupsQuery.groups,
      weekProjectGroupsQuery.groups,
      allTasksProjectGroupsQuery.groups,
      allTasksUnscheduledQuery.tasks,
    ]
  );
  // Project Tasks' member-task-nudge effect (and anything else a caller
  // needs to scan loaded tasks for) has no query state of its own to read
  // now that this view owns all of it — surface the same set used for the
  // filter picker options.
  useEffect(() => {
    onTasksLoaded?.(allLoadedTasks);
  }, [allLoadedTasks, onTasksLoaded]);

  const knownProjectsRef = useRef(
    new Map<string, ReturnType<typeof buildProjectOptions>[number]>()
  );
  const knownAssigneesRef = useRef(
    new Map<string, ReturnType<typeof buildAssigneeOptions>[number]>()
  );

  // Group *card* order freeze (TA-10446 follow-up): while a completeTask
  // toggle is in flight, the optimistic cache patch (and later the
  // refetchQueries landing) both change taskActivityAt for the toggled
  // task's group, which would otherwise re-sort composeServerGroups'
  // lastActivityAt-desc order and visibly shuffle PROJECT GROUP cards mid-tap.
  // Row/bucket content still updates live (see composeServerGroups); only the
  // *positions* of the group cards are pinned to their pre-toggle order until
  // every in-flight toggle has fully settled (mutation + its refetches).
  const pendingToggleTaskIdsRef = useRef(new Set<string>());
  // Concurrent completions (TA-11016). This used to be a single-flight lock:
  // rapid taps across rows fired overlapping optimistic updates + refetches,
  // which made each group card's completed/total progress bar jump between
  // stale and fresh counts, so every other row was blocked until the first
  // toggle's mutation AND its refetches settled — 6–9 s of dead circles on a
  // large account. Now any number of rows can be in flight; what stays
  // serialized is the refetch (see flushSettledToggles), which is what the
  // progress-bar race actually came from.
  // completingTaskIdsRef is the synchronous guard against a repeat tap on a
  // row that is already in flight (a double tap would otherwise toggle it
  // straight back). There is deliberately no in-flight spinner: per the
  // prototype the row moves to its new section the moment it is tapped (the
  // optimistic completedAt re-buckets it, see composeServerGroups), and a
  // rejected toggle is rolled back by Apollo and reported (TA-11016).
  const completingTaskIdsRef = useRef(new Set<string>());
  // The completedAt each toggled task should show until a refetch requested
  // after its mutation settled has landed (see CompletedAtOverrides): a
  // response requested earlier — an earlier batch's refetch, a poll — would
  // otherwise flip a rapidly tapped task back open for a moment. The version
  // counter re-runs the groups memo when an entry is added or dropped.
  const completedAtOverridesRef = useRef(new Map<string, string | null>());
  const [completionOverridesVersion, setCompletionOverridesVersion] =
    useState(0);
  const bumpCompletionOverrides = useCallback(
    () => setCompletionOverridesVersion((v) => v + 1),
    []
  );
  // completeTask mutations still waiting on the server.
  const inFlightCompletionsRef = useRef(0);
  // Toggles whose mutation has settled but whose rows stay pinned until the
  // next coalesced refetch lands.
  const awaitingRefetchIdsRef = useRef(new Set<string>());
  const refetchInFlightRef = useRef(false);
  // The off-critical-path listTasks refetch is serialized the same way, so two
  // of its responses can't land out of order.
  const listTasksRefetchInFlightRef = useRef(false);
  // Refetch passes each toggled task has been through without the cache
  // showing its new state yet (see flushSettledToggles).
  const releaseAttemptsRef = useRef(new Map<string, number>());
  const listTasksRefetchQueuedRef = useRef(false);
  // Keyed by the same list scope as the row pin below, not by tab alone —
  // card order is just as list-specific as row order (a filter change while a
  // toggle is in flight would otherwise pin the new card set to the old
  // list's ordering).
  //
  // While any toggle is in flight every card holds its place (a new one is
  // slotted in by the computed order). Once nothing is pending, only the
  // cards this session's own taps touched keep their position (TA-11016, per
  // design: completing a task is activity and used to lift its card to the
  // top); every other card follows the computed/server order again, so e.g.
  // a teammate's activity on another project still reorders the list.
  // Pull-to-refresh (handleRefresh) drops both for the list.
  //
  // It runs on the list as displayed — after My Day/7 Days drop fully
  // completed cards and All Tasks sinks them — so a pinned index is a
  // position the user actually saw. Pinning the pre-filter list let hidden
  // cards shift the visible ones, and on All Tasks a reopened card jumped out
  // of the completed tail; per QA a card stays put after a complete or a
  // reopen until pull-to-refresh (TA-11016).
  const groupOrderByScopeRef = useRef(new Map<string, string[]>());
  const toggledGroupKeysByScopeRef = useRef(new Map<string, Set<string>>());
  const stabilizeGroupOrder = useCallback(
    (computedGroups: MyTaskGroup[], scope: string): MyTaskGroup[] => {
      const prevOrder = groupOrderByScopeRef.current.get(scope);
      const touched = toggledGroupKeysByScopeRef.current.get(scope);
      let ordered = computedGroups;
      if (prevOrder && pendingToggleTaskIdsRef.current.size > 0) {
        ordered = applyStickyOrder(computedGroups, prevOrder, (g) => g.key);
      } else if (prevOrder && touched && touched.size > 0) {
        ordered = applyPinnedPositions(
          computedGroups,
          prevOrder,
          touched,
          (g) => g.key
        );
      }
      groupOrderByScopeRef.current.set(
        scope,
        ordered.map((g) => g.key)
      );
      return ordered;
    },
    []
  );

  const [completeTask, completeTaskResult] = useCompleteTaskMutation();
  // Read from a ref, not a dep: `completeTaskResult` gets a new identity on
  // every mutation state change, which would churn handleToggleCompleteTask's
  // identity and with it renderGroup's, re-rendering every group card.
  const apolloClientRef = useRef(completeTaskResult?.client);
  apolloClientRef.current = completeTaskResult?.client;

  // A touched card the refetched pages no longer contain stays on screen
  // (see retainTouchedGroups) until pull-to-refresh drops it, so the pin
  // above still has a card to hold in place.
  const retainedGroupsByScopeRef = useRef(
    new Map<string, Map<string, ProjectGroupInput>>()
  );
  const withRetainedGroups = useCallback(
    (serverGroups: ProjectGroupInput[], scope: string) => {
      let retained = retainedGroupsByScopeRef.current.get(scope);
      if (!retained) {
        retained = new Map();
        retainedGroupsByScopeRef.current.set(scope, retained);
      }
      return retainTouchedGroups(
        serverGroups,
        groupByOption,
        toggledGroupKeysByScopeRef.current.get(scope),
        retained,
        (taskId) => readLiveCompletedAt(apolloClientRef.current?.cache, taskId)
      );
    },
    [groupByOption]
  );

  // Row order freeze (TA-10189): the same pin, one level down. While a toggle
  // is in flight the optimistic patch and the refetch landing can each
  // reshuffle the remaining rows (composeServerGroups reads a group as
  // `[...tasks, ...completedTasks]`, and the refetch moves the toggled task
  // to the other connection), so the rows the user is looking at keep their
  // positions until the toggle has fully settled.
  // The snapshot is only refreshed while nothing is pending, so it always
  // holds the pre-toggle order for the whole in-flight window.
  // A row that is new to a bucket (the task just completed or reopened) goes
  // straight to its computed position rather than the end of the list
  // (applyStickyOrder): parked at the end, a reopened task sat at the bottom
  // of the open rows and then jumped to its sorted place once the refetch
  // released the pin (TA-11016).
  // A group key alone is NOT enough to identify a snapshot: the same
  // `project:p1` card exists on every tab and under every filter/sort/groupBy
  // combination, each holding a different subset of that project's tasks in a
  // different order. Pinning across such a change would reorder the new list
  // by another list's ordering — e.g. complete a task on 7 Days, switch to
  // All Tasks before the toggle settles, and All Tasks' rows get re-sorted by
  // the 7 Days snapshot (a due-date-filtered subset), pushing every task
  // absent from it — overdue ones included — below the rest until the pending
  // window clears. So the snapshot is scoped, and a scope change simply
  // re-captures instead of pinning: whatever the user first sees on the new
  // list becomes its own pinned order, which is exactly the no-jump guarantee
  // one scope down.
  const taskOrderScopeRef = useRef<string | null>(null);
  const taskOrderByGroupRef = useRef(
    new Map<string, { tasks: string[]; completedTasks: string[] }>()
  );
  const stabilizeTaskOrder = useCallback(
    (computedGroups: MyTaskGroup[], scope: string): MyTaskGroup[] => {
      if (
        pendingToggleTaskIdsRef.current.size > 0 &&
        taskOrderScopeRef.current === scope
      ) {
        return computedGroups.map((group) => {
          const prevOrder = taskOrderByGroupRef.current.get(group.key);
          if (!prevOrder) return group;
          return {
            ...group,
            tasks: applyStickyOrder(group.tasks, prevOrder.tasks, (t) => t.id),
            completedTasks: applyStickyOrder(
              group.completedTasks,
              prevOrder.completedTasks,
              (t) => t.id
            ),
          };
        });
      }
      taskOrderScopeRef.current = scope;
      taskOrderByGroupRef.current = new Map(
        computedGroups.map((group) => [
          group.key,
          {
            tasks: group.tasks.map((t) => t.id),
            completedTasks: group.completedTasks.map((t) => t.id),
          },
        ])
      );
      return computedGroups;
    },
    []
  );

  // Project Tasks hides the Projects row entirely — no need to build these.
  const projectOptions = useMemo(() => {
    if (projectId) return [];
    buildProjectOptions(allLoadedTasks).forEach((option) =>
      knownProjectsRef.current.set(option.id, option)
    );
    return [...knownProjectsRef.current.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allLoadedTasks, projectId]);
  const assigneeOptions = useMemo(() => {
    buildAssigneeOptions(allLoadedTasks).forEach((option) =>
      knownAssigneesRef.current.set(option.id, option)
    );
    return [...knownAssigneesRef.current.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allLoadedTasks]);

  const heroTasks = useMemo(
    () => selectHeroTasks(heroOverdueTasksQuery.tasks),
    [heroOverdueTasksQuery.tasks]
  );

  // Which list the order snapshots below belong to: the tab actually being
  // composed (deferred, so it stays in lockstep with the groups themselves)
  // plus every variable that changes what the list contains.
  const listScope = `${deferredActiveTab}|${filterSignature}`;

  // 7 Days and All Tasks each come from ONE server-sorted query, so a
  // client-side re-sort has to agree with the server's rule or it silently
  // overrides it. Only OverdueToLow does — see canSortGroupTasksOnClient.
  const maybeSortGroupTasks = useCallback(
    (composed: MyTaskGroup[]): MyTaskGroup[] =>
      canSortGroupTasksOnClient(orderByOption)
        ? sortGroupTasks(composed, orderByOption)
        : composed,
    [orderByOption]
  );

  const { groups: composedGroups, unscheduledGroup } = useMemo((): {
    groups: MyTaskGroup[];
    unscheduledGroup: MyTaskGroup | null;
  } => {
    switch (deferredActiveTab) {
      case MyTasksTab.MyDay: {
        const merged = mergeServerGroups(
          groupByOption,
          myDayOverdueProjectGroupsQuery.groups,
          myDayTodayProjectGroupsQuery.groups
        );
        return {
          // TA-10189 AC1: a fully-completed group card disappears entirely
          // on My Day rather than sinking to the bottom (that's All Tasks'
          // rule below) — the header's "N projects" count is groups.length,
          // so excluding here decrements it for free.
          groups: stabilizeGroupOrder(
            excludeFullyCompleteGroups(
              stabilizeTaskOrder(
                excludeEmptyGroups(
                  sortGroupTasks(
                    composeServerGroups(
                      withRetainedGroups(merged, listScope),
                      groupByOption,
                      completedAtOverridesRef.current
                    ),
                    orderByOption
                  )
                ),
                listScope
              )
            ),
            listScope
          ),
          unscheduledGroup: null,
        };
      }
      case MyTasksTab.SevenDays:
        // 7 Days shows no completed and no overdue items (AC3). TA-10189
        // AC1 applies the same disappear-on-complete rule as My Day.
        return {
          groups: stabilizeGroupOrder(
            excludeFullyCompleteGroups(
              stabilizeTaskOrder(
                excludeEmptyGroups(
                  // Single server-sorted query: only re-sort where the client
                  // rule matches the server's (Jack's review, finding 1).
                  // A task completed here leaves at once rather than showing
                  // under "Show completed" until the refetch drops it.
                  withoutCompletedTasks(
                    maybeSortGroupTasks(
                      composeServerGroups(
                        withRetainedGroups(
                          weekProjectGroupsQuery.groups,
                          listScope
                        ),
                        groupByOption,
                        completedAtOverridesRef.current
                      )
                    )
                  )
                ),
                listScope
              )
            ),
            listScope
          ),
          unscheduledGroup: null,
        };
      case MyTasksTab.AllTasks: {
        const unscheduledGroup = composeUnscheduledGroup(
          allTasksUnscheduledQuery.tasks,
          completedAtOverridesRef.current
        );

        return {
          // TA-10189 AC2: All Tasks sinks a fully-completed group card to
          // the bottom instead of removing it (My Day/7 Days' rule above);
          // a card the user's own tap completed or reopened holds its place
          // until pull-to-refresh (stabilizeGroupOrder, TA-11016).
          // TA-10495 #10: Unassigned then pins to the front, right after the
          // Unscheduled card rendered separately above this list.
          groups: pinUnassignedFirst(
            stabilizeGroupOrder(
              sortFullyCompleteGroupsLast(
                stabilizeTaskOrder(
                  excludeEmptyGroups(
                    // Same single-query rule as 7 Days above.
                    maybeSortGroupTasks(
                      composeServerGroups(
                        withRetainedGroups(
                          allTasksProjectGroupsQuery.groups,
                          listScope
                        ),
                        groupByOption,
                        completedAtOverridesRef.current
                      )
                    )
                  ),
                  listScope
                )
              ),
              listScope
            )
          ),
          unscheduledGroup,
        };
      }
    }
  }, [
    deferredActiveTab,
    groupByOption,
    // My Day re-sorts in every mode; the other two go through
    // maybeSortGroupTasks, which is keyed on Order By as well.
    orderByOption,
    maybeSortGroupTasks,
    listScope,
    stabilizeGroupOrder,
    stabilizeTaskOrder,
    withRetainedGroups,
    allTasksUnscheduledQuery.tasks,
    allTasksProjectGroupsQuery.groups,
    myDayOverdueProjectGroupsQuery.groups,
    myDayTodayProjectGroupsQuery.groups,
    weekProjectGroupsQuery.groups,
    // Stands in for completedAtOverridesRef, whose contents React can't see.
    completionOverridesVersion,
  ]);

  // Progress follows the taps (TA-11016). A group's taskCount/completedCount
  // are server totals, adjusted for re-bucketed rows; during a burst of taps
  // the responses landing in between (earlier batches' refetches, polls, and
  // on My Day two separately-timed queries) are snapshots of different
  // moments, so the bar stepped back and forth. On a group's first tap its
  // shown counts become the baseline; every tap then moves them by one, and
  // nothing else does, until the group's last toggle has been confirmed by a
  // refetch requested after it (releaseToggles) — then the server's count,
  // which by then says the same, takes over. Keyed per list, like the order
  // pins: the same project card carries different counts on each tab.
  const progressPinsRef = useRef(
    new Map<
      string,
      {
        taskCount: number;
        completedCount: number;
        /** Toggles of each task still waiting to be confirmed. */
        pendingIds: Map<string, number>;
      }
    >()
  );
  const listScopeRef = useRef(listScope);
  listScopeRef.current = listScope;
  const groups = useMemo(
    () =>
      composedGroups.map((group) => {
        const pin = progressPinsRef.current.get(`${listScope}|${group.key}`);
        return pin
          ? {
              ...group,
              taskCount: pin.taskCount,
              completedCount: pin.completedCount,
            }
          : group;
      }),
    // completionOverridesVersion also bumps whenever a pin changes.
    [composedGroups, listScope, completionOverridesVersion]
  );
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  /** Moves the tapped task's group progress by one tap; returns an undo. */
  const pinProgressForTap = useCallback(
    (taskId: string, completing: boolean): (() => void) => {
      const group = groupsRef.current.find(
        (g) =>
          g.tasks.some((t) => t.id === taskId) ||
          g.completedTasks.some((t) => t.id === taskId)
      );
      if (!group) return () => {};
      const scope = listScopeRef.current;
      const touched =
        toggledGroupKeysByScopeRef.current.get(scope) ?? new Set<string>();
      touched.add(group.key);
      toggledGroupKeysByScopeRef.current.set(scope, touched);
      const key = `${scope}|${group.key}`;
      const pins = progressPinsRef.current;
      const pin = pins.get(key) ?? {
        taskCount: group.taskCount ?? group.tasks.length,
        completedCount: group.completedCount ?? group.completedTasks.length,
        pendingIds: new Map<string, number>(),
      };
      const step = completing ? 1 : -1;
      pin.taskCount = Math.max(0, pin.taskCount - step);
      pin.completedCount = Math.max(0, pin.completedCount + step);
      pin.pendingIds.set(taskId, (pin.pendingIds.get(taskId) ?? 0) + 1);
      pins.set(key, pin);
      return () => {
        const current = pins.get(key);
        if (!current) return;
        current.taskCount = Math.max(0, current.taskCount + step);
        current.completedCount = Math.max(0, current.completedCount - step);
        // Only this toggle is undone: an earlier toggle of the same task may
        // still be waiting for its refetch.
        const left = (current.pendingIds.get(taskId) ?? 1) - 1;
        if (left > 0) current.pendingIds.set(taskId, left);
        else current.pendingIds.delete(taskId);
        if (current.pendingIds.size === 0) pins.delete(key);
      };
    },
    []
  );

  // Only ever called for a batch whose refetch was requested after every
  // toggle in it had settled, so the cache now holds their real state and
  // the overrides can go.
  const releaseToggles = useCallback(
    (ids: string[]) => {
      ids.forEach((id) => {
        pendingToggleTaskIdsRef.current.delete(id);
        completedAtOverridesRef.current.delete(id);
        releaseAttemptsRef.current.delete(id);
      });
      progressPinsRef.current.forEach((pin, key) => {
        ids.forEach((id) => pin.pendingIds.delete(id));
        if (pin.pendingIds.size === 0) progressPinsRef.current.delete(key);
      });
      bumpCompletionOverrides();
    },
    [bumpCompletionOverrides]
  );

  /**
   * Fire-and-forget listTasks refetch, never two on the wire: a request made
   * while one is in flight is folded into a single follow-up that starts once
   * it lands. Rows never wait on this (see flushSettledToggles).
   */
  const refetchListTasks = useCallback(async () => {
    if (listTasksRefetchInFlightRef.current) {
      listTasksRefetchQueuedRef.current = true;
      return;
    }
    listTasksRefetchInFlightRef.current = true;
    try {
      do {
        listTasksRefetchQueuedRef.current = false;
        const client = apolloClientRef.current;
        // Never rejects. Off the critical path: the next poll/focus refetch
        // reconciles a failure.
        if (client) await refetchActiveQueriesFresh(client, ['listTasks']);
      } while (listTasksRefetchQueuedRef.current);
    } finally {
      listTasksRefetchInFlightRef.current = false;
    }
  }, []);

  /** See isCompletionConfirmedInCache: dropping this check in favour of the
   * fresh refetch alone flipped tasks back open on device (TA-11016). */
  const isToggleConfirmedInCache = useCallback(
    (id: string): boolean =>
      !completedAtOverridesRef.current.has(id) ||
      isCompletionConfirmedInCache(
        apolloClientRef.current?.cache,
        id,
        completedAtOverridesRef.current.get(id)
      ),
    []
  );

  /**
   * One refetch for every toggle that has settled, once no completeTask is
   * still on the wire, and never two at once (TA-11016). Each toggle used to
   * await its own name-based refetch; overlapping ones landed out of order and
   * made the progress bar jump between stale and fresh counts, which is what
   * the old single-flight lock guarded against. Coalescing keeps that
   * guarantee without blocking the next tap.
   *
   * Only the two queries THIS screen renders from are awaited, because the
   * await gates how long the card/row order pins stay on:
   * listMyTasksProjectGroups feeds the server-grouped tabs — its groups carry
   * their own taskCount/completedCount for MyTaskGroupCard's progress bar,
   * which a Task-level cache write can't recompute on its own — and
   * listTasksLight feeds the Unscheduled card
   * (useListUnscheduledTasksFromQuery). `listTasks` (other screens' full task
   * lists: ProjectTasksScreen, TaskModal, …) is refetched too, but off the
   * critical path — it's the heaviest of the three (full TaskDetail payload)
   * and awaiting it kept the row pinned for the extra half-second-plus after
   * this screen's own data had already landed (TA-10189).
   */
  const flushSettledToggles = useCallback(async () => {
    if (refetchInFlightRef.current) return;
    refetchInFlightRef.current = true;
    try {
      // A toggle that settles while a refetch is on the wire joins the next
      // pass; one that is still in flight flushes when it settles.
      while (
        inFlightCompletionsRef.current === 0 &&
        awaitingRefetchIdsRef.current.size > 0
      ) {
        const batch = [...awaitingRefetchIdsRef.current];
        awaitingRefetchIdsRef.current.clear();
        // Each query goes out as its own request instead of joining an
        // identical one already in flight (TA-11018), so it can't inherit a
        // snapshot taken before these toggles reached the server. Never
        // rejects: on a failed refetch the confirm check below decides.
        const client = apolloClientRef.current;
        if (client) {
          await refetchActiveQueriesFresh(client, [
            'listTasksLight',
            'listMyTasksProjectGroups',
          ]);
        }
        // Fire-and-forget, deliberately outside the awaited set above.
        void refetchListTasks();
        // Release only what the refetched data confirms; the rest keep their
        // override (rows, order and progress stay put) for another pass. A
        // task the server still disagrees with after MAX_RELEASE_ATTEMPTS is
        // released anyway, so a real server-side outcome isn't masked.
        const confirmed: string[] = [];
        const unconfirmed: string[] = [];
        batch.forEach((id) => {
          const attempts = (releaseAttemptsRef.current.get(id) ?? 0) + 1;
          releaseAttemptsRef.current.set(id, attempts);
          if (
            isToggleConfirmedInCache(id) ||
            attempts >= MAX_RELEASE_ATTEMPTS
          ) {
            confirmed.push(id);
          } else {
            unconfirmed.push(id);
          }
        });
        releaseToggles(confirmed);
        if (unconfirmed.length > 0) {
          unconfirmed.forEach((id) => awaitingRefetchIdsRef.current.add(id));
          // Give a deduplicated or late request time to finish first.
          await new Promise((resolve) =>
            setTimeout(resolve, UNCONFIRMED_RETRY_MS)
          );
        }
      }
    } finally {
      refetchInFlightRef.current = false;
    }
  }, [refetchListTasks, releaseToggles, isToggleConfirmedInCache]);

  // One feedback toast at a time: a burst of quick completions replaces the
  // previous "Task Completed" instead of queuing one per tap.
  const feedbackToastIdRef = useRef<string | null>(null);
  const showFeedbackToast = useCallback(
    (message: string) => {
      if (feedbackToastIdRef.current) {
        toast.hide?.(feedbackToastIdRef.current);
      }
      feedbackToastIdRef.current =
        toast.show(message, { duration: 3000 }) ?? null;
    },
    [toast]
  );

  const handleToggleCompleteTask = useCallback(
    (task: Task) => {
      const { id, completedAt, projectId: taskProjectId } = task;
      // Only a row whose own completeTask is still on the wire ignores the
      // tap (completeTask is a server-side toggle, so two at once would cancel
      // out). Once it returns the row takes taps again — e.g. reopening a task
      // completed by mistake, or retrying after a failure — even while the
      // batch's refetch is still pending (TA-11016).
      if (completingTaskIdsRef.current.has(id)) return;
      completingTaskIdsRef.current.add(id);
      const hadOverride = completedAtOverridesRef.current.has(id);
      const previousOverride = completedAtOverridesRef.current.get(id);
      const nextCompletedAt = completedAt ? null : new Date().toISOString();
      completedAtOverridesRef.current.set(id, nextCompletedAt);
      releaseAttemptsRef.current.delete(id);
      const undoProgress = pinProgressForTap(id, !completedAt);
      bumpCompletionOverrides();
      // Held until the mutation AND the coalesced refetch have both settled
      // (see flushSettledToggles) so stabilizeGroupOrder keeps PROJECT GROUP
      // cards pinned in place through both the optimistic-cache reorder and
      // the refetch-landing reorder, instead of unfreezing between them.
      pendingToggleTaskIdsRef.current.add(id);
      inFlightCompletionsRef.current += 1;
      // The row has already moved (optimistic completedAt), so a rejection
      // must say so: Apollo rolls the row back, and this reports it unless
      // errorLink already shows its own message. Success feedback and
      // tracking wait for the server — with errorPolicy 'all' an onCompleted
      // callback would also fire for a rejected response.
      const reportFailure = (errorMessage: string, needsMessage: boolean) => {
        // Let Apollo's rollback show through — or, if an earlier toggle of
        // this task is still waiting for its refetch, go back to that one's
        // state rather than to whatever stale data the cache holds.
        if (hadOverride) {
          completedAtOverridesRef.current.set(id, previousOverride ?? null);
        } else {
          completedAtOverridesRef.current.delete(id);
        }
        undoProgress();
        bumpCompletionOverrides();
        logError({
          message: 'completeTask failed',
          errorMessage,
          operationName: 'completeTask',
          extra: [{ taskId: id }],
        });
        if (needsMessage) {
          toast.show(tShared('actionFailedTryAgain'), {
            type: 'danger',
            duration: 4000,
          });
        }
      };
      Promise.resolve(
        completeTask({
          variables: { id },
          optimisticResponse: {
            completeTask: {
              __typename: 'Task',
              id,
              completedAt: nextCompletedAt,
              status: completedAt ? TaskStatus.Ongoing : TaskStatus.Completed,
            },
          },
        })
      )
        .then((result) => {
          if (result?.errors?.length) {
            reportFailure(
              result.errors.map((e) => e.message).join('; '),
              completeTaskFailureNeedsMessage({ errors: result.errors })
            );
            return;
          }
          // Keep the server's own timestamp until the release refetch lands.
          const confirmed = result?.data?.completeTask;
          if (confirmed && completedAtOverridesRef.current.has(id)) {
            completedAtOverridesRef.current.set(
              id,
              confirmed.completedAt ?? null
            );
          }
          if (completedAt) {
            trackEvent(MIXPANEL_EVENTS.TASK_REOPENED, {
              project_id: taskProjectId,
              task_id: id,
            });
            showFeedbackToast('Task Reopened');
          } else {
            trackEvent(MIXPANEL_EVENTS.TASK_COMPLETED, {
              project_id: taskProjectId,
              task_id: id,
              platform: Platform.OS,
            });
            showFeedbackToast('Task Completed');
            onTaskCompleted?.();
          }
        })
        .catch((err: unknown) => {
          reportFailure(
            err instanceof Error ? err.message : String(err),
            completeTaskFailureNeedsMessage({ rejection: err })
          );
        })
        .finally(() => {
          inFlightCompletionsRef.current -= 1;
          completingTaskIdsRef.current.delete(id);
          awaitingRefetchIdsRef.current.add(id);
          void flushSettledToggles();
        });
    },
    [
      completeTask,
      trackEvent,
      toast,
      tShared,
      onTaskCompleted,
      flushSettledToggles,
      bumpCompletionOverrides,
      pinProgressForTap,
      showFeedbackToast,
    ]
  );

  // The sheet is a transparentModal route (with its own
  // BottomSheetModalProvider); it reads/writes whichever filter context
  // `filterScope` points at directly, so only the picker options + scope
  // travel via params.
  const handleFilterPress = useCallback(() => {
    navigation.navigate('my-tasks-filter-modal', {
      projectOptions,
      assigneeOptions,
      hideProjectsFilter,
      scope: filterScope,
    });
  }, [
    navigation,
    projectOptions,
    assigneeOptions,
    hideProjectsFilter,
    filterScope,
  ]);

  // My Tasks only: MainHeader's popup filter/sort action opens this same
  // sheet — sort still points at the legacy modal until the revamp replaces
  // it end-to-end. ProjectTasksScreen never passes headerEvent (its header
  // doesn't dispatch these).
  useEffect(() => {
    if (!headerEvent) return;
    if (headerEvent.type === 'doSortTaskList') {
      navigation.navigate('tasks-sort-modal', { projectId: undefined });
    } else if (headerEvent.type === 'doFilterTaskList') {
      handleFilterPress();
    }
  }, [headerEvent?.ts]);

  const hasActiveFilters =
    filterProjectIds.length > 0 ||
    filterAssigneeIds.length > 0 ||
    priorityFilters.length > 0 ||
    dueFilterRange !== null;

  const queriesForTab = useMemo(() => {
    switch (deferredActiveTab) {
      case MyTasksTab.MyDay:
        return [myDayOverdueProjectGroupsQuery, myDayTodayProjectGroupsQuery];
      case MyTasksTab.SevenDays:
        return [weekProjectGroupsQuery];
      case MyTasksTab.AllTasks:
        return [allTasksProjectGroupsQuery, allTasksUnscheduledQuery];
    }
  }, [
    deferredActiveTab,
    myDayOverdueProjectGroupsQuery,
    myDayTodayProjectGroupsQuery,
    weekProjectGroupsQuery,
    allTasksUnscheduledQuery,
    allTasksProjectGroupsQuery,
  ]);

  // Per-group "load more tasks" — only 7 Days / All Tasks, whose groups each
  // come from a single query. My Day merges Overdue + Current due_group-
  // scoped queries per group, so which scope's cursor a "load more" should
  // continue is ambiguous — not supported there.
  let fetchMoreGroupTasksForActiveTab:
    | typeof weekProjectGroupsQuery.fetchMoreGroupTasks
    | undefined;
  if (isSevenDaysServerGrouped) {
    fetchMoreGroupTasksForActiveTab =
      weekProjectGroupsQuery.fetchMoreGroupTasks;
  } else if (isAllTasksServerGrouped) {
    fetchMoreGroupTasksForActiveTab =
      allTasksProjectGroupsQuery.fetchMoreGroupTasks;
  }

  // Applying a filter change must always pull live data, not just satisfy
  // Apollo's cache-first policy — e.g. switching Order By to Created Date
  // and back to the default re-uses the exact variables from this screen's
  // very first load, which cache-first serves straight from cache with no
  // network round trip at all, silently showing however-stale that snapshot
  // is. Force a real refetch (mirrors handleRefresh/pull-to-refresh below)
  // whenever the combined filter signature actually changes; skip the first
  // render so mount's own initial fetch isn't duplicated.
  const previousFilterSignatureRef = useRef(filterSignature);
  useEffect(() => {
    if (previousFilterSignatureRef.current === filterSignature) return;
    previousFilterSignatureRef.current = filterSignature;
    queriesForTab.forEach((query) => query.refetch());
  }, [filterSignature, queriesForTab]);

  // Coming back from task detail must show the edit (TA-10495 QA round 2):
  // changing a task's priority there moves it between cards and within its
  // card, and the mutation's name-based `refetchQueries` only reaches queries
  // Apollo considers active — a tab whose query is skipped sits in `standby`
  // and is excluded, so it kept serving its pre-edit group edges. `stalenessMs
  // = 0` because this is a stack pop, not web tab switching: the storm the
  // default window guards against can't happen here, and a detail → back
  // round-trip is exactly the case that has to refetch.
  //
  // Read the active tab's queries from a ref so this callback keeps one
  // identity for the component's whole life. `queriesForTab` is rebuilt
  // whenever any of its query results change (every loading/data transition),
  // and useFocusEffect re-runs its effect whenever the callback identity
  // changes — with stalenessMs = 0 that would refetch on essentially every
  // render rather than on focus.
  const queriesForTabRef = useRef(queriesForTab);
  queriesForTabRef.current = queriesForTab;
  const refetchActiveTab = useCallback(
    () =>
      Promise.all(
        queriesForTabRef.current
          // Apollo's `refetch()` ignores `skip`, and some of these queries are
          // skipped precisely because their variables would be invalid — the
          // 7 Days query stands down when the due-date filter leaves an empty
          // intersection rather than ask the server for a backwards range.
          // A query that has never delivered a response has no `pageInfo`,
          // which is the same "never fetched" signal `initialLoading` uses
          // below, so refetching only the ones that have fetched keeps this
          // hook off those paths (Jack's review, finding 5).
          .filter((query) => query.pageInfo !== undefined)
          // At the loaded depth: `refetch()` asks for page 1 only, which
          // replaced every page onEndReached had added; the list then loaded
          // them again and jumped (TA-11217, same as the TA-11016 poll).
          .map((query) => query.refetchAtLoadedDepth())
      ),
    []
  );
  useRefetchOnFocus(refetchActiveTab, 0);

  const handleEndReached = useCallback(() => {
    queriesForTab.forEach((query) => {
      if (query.pageInfo?.hasNextPage && !query.loading) {
        query.fetchFromCursor();
      }
    });
  }, [queriesForTab]);

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(() => {
    // An explicit refresh asks for the server's view, including its card
    // order — drop this list's snapshot and pins (see stabilizeGroupOrder).
    groupOrderByScopeRef.current.delete(listScope);
    toggledGroupKeysByScopeRef.current.delete(listScope);
    retainedGroupsByScopeRef.current.delete(listScope);
    setRefreshing(true);
    Promise.all(queriesForTab.map((query) => query.refetch())).finally(() =>
      setRefreshing(false)
    );
  }, [listScope, queriesForTab]);

  // Deliberately does NOT gate on `groups.length === 0` — My Day merges two
  // independent queries (Overdue + Current), and if one resolves before the
  // other, the merged `groups` briefly becomes non-empty from that one
  // query's partial results alone. Gating on `groups.length` would treat
  // that partial merge as "already loaded" and render it, then swap in the
  // real (complete) data once the second query lands — that's exactly the
  // "shows two projects, then replaced by the correct My Day data" bug.
  // Every query relevant to the active tab must have delivered at least one
  // response (`pageInfo` defined) before we render anything; a query that
  // just un-skipped (tab just became active) doesn't flip `loading` to true
  // in the same render, so `pageInfo === undefined` is the reliable signal,
  // not `loading` alone. A `refetch()` (pull-to-refresh) keeps the previous
  // `pageInfo` in place while in flight, so this doesn't reintroduce a
  // spinner flash there.
  // allTasksUnscheduledQuery is deliberately, permanently skipped whenever a
  // due-date filter is active (Unscheduled tasks have no due date to match)
  // — its `pageInfo` then stays undefined forever, not just while a fetch is
  // in flight. Without this carve-out, the `pageInfo === undefined` signal
  // below can't tell "still loading" apart from "will never fetch", and
  // initialLoading gets stuck true for the rest of the All Tasks tab's life.
  const unscheduledQuerySkippedByFilter = dueFilterRange !== null;
  // heroOverdueTasksQuery is deliberately NOT part of queriesForTab (it has
  // its own pagination that nothing needs — Hero only ever shows the top 5 —
  // so folding it into queriesForTab would make handleEndReached/pull-to-
  // refresh needlessly fetch more pages of it). But it IS a separate,
  // independently-timed network request from myDayOverdueProjectGroupsQuery/
  // myDayTodayProjectGroupsQuery, and without this check, `initialLoading`
  // could clear (list/spinner swap to real content) the moment those two
  // resolve while heroOverdueTasksQuery is still in flight — Hero would then
  // pop in on its own later, after the rest of the tab already painted. Given
  // this screen also defers My Day's un-skip behind `deferredActiveTab` (see
  // eee29a13 — traded for avoiding tab-switch jank), that later Hero-only
  // render can land in a low-priority React lane RN's scheduler doesn't
  // reliably flush without another touch event — visibly leaving Hero's
  // reserved space blank until the user taps the screen. Waiting on this
  // query here keeps Hero's data ready in the exact same commit as the rest
  // of My Day, so there's no separate render for it to get stuck in.
  const heroQueryStillLoading =
    deferredActiveTab === MyTasksTab.MyDay &&
    heroOverdueTasksQuery.pageInfo === undefined;
  // While `deferredActiveTab` hasn't caught up to `activeTab` yet, every
  // memo/query above (groups, unscheduledGroup, heroTasks, queriesForTab...)
  // still reflects the tab being switched AWAY FROM — that's the whole point
  // of deferring them (TA-10187, avoids stalling the tab-press feedback).
  // With a small dataset that catch-up is imperceptible, but with enough
  // cached data across all 3 tabs, composeServerGroups/stabilizeGroupOrder
  // recomputing for the new tab can take long enough (1-2s) that the user
  // taps "All Tasks" and keeps looking at the PREVIOUS tab's content the
  // whole time — indistinguishable from the tap not having registered.
  // Showing the spinner for this gap instead trades that confusing stale
  // content for an honest, brief loading state — the new tab's own queries
  // are typically already cached (pageInfo defined, loading false) by the
  // time this clears, since the switch only waits on `deferredActiveTab`
  // catching up, not on a real network round trip.
  const isTabSwitchPending = activeTab !== deferredActiveTab;
  const initialLoading =
    isTabSwitchPending ||
    (!unscheduledGroup &&
      (heroQueryStillLoading ||
        queriesForTab.some((query) => {
          if (
            query === allTasksUnscheduledQuery &&
            unscheduledQuerySkippedByFilter
          ) {
            return false;
          }
          // `loading` also becomes true for background re-observation (for
          // example when opening a transparent modal changes this screen's
          // focus and disables polling). Once pageInfo exists, the list has
          // already delivered its initial response and must remain visible.
          return query.pageInfo === undefined;
        })));
  const showInitialSkeleton = useMinimumVisible(initialLoading);

  const showHero =
    deferredActiveTab === MyTasksTab.MyDay && heroTasks.length > 0;
  // Hero stays a normal child of the FlatList's header (not a separate
  // sibling layer) so native nested-scroll touch arbitration keeps working —
  // vertical drags go to the outer list, horizontal drags to the hero's own
  // carousel. It's taken out of flow (position: absolute + a same-height
  // spacer standing in for it) and given a scroll-compensating translateY so
  // it visually stays pinned at the top while the list content scrolls; once
  // the list's own content (rendered after it, so painted on top) reaches
  // that position, it naturally covers the hero as scrolling continues.
  const [heroHeight, setHeroHeight] = useState(0);
  // heroHeight only updates via the Hero wrapper's onLayout, so a refetch
  // that changes the hero's task set must trigger a fresh measurement. The
  // wrapper is keyed on the hero task ids (see listHeader) so a new set
  // remounts it, and a mount always fires onLayout.
  //
  // TA-11016: this used to reset heroHeight to 0 instead. The wrapper is
  // absolutely positioned at top 0, so a new task set at the same height
  // (e.g. a completed hero task replaced by the next overdue one) leaves its
  // frame unchanged and onLayout never fires again. heroHeight stayed 0, the
  // spacer collapsed, and the list sheet (zIndex 1) painted over the hero:
  // the carousel vanished until a tab switch remounted it. Keeping the last
  // height until the remount re-measures also avoids a one-frame collapse.
  //
  // Keyed on the actual set/order of hero task ids, not the `heroTasks` array
  // reference: refetchQueries hands back a fresh array on every completion
  // screen-wide, even when the hero's real content is unchanged.
  const heroTasksKey = heroTasks.map((task) => task.id).join(',');
  const scrollY = useRef(new Animated.Value(0)).current;
  const heroTranslateY =
    heroHeight > 0
      ? scrollY.interpolate({
          inputRange: [0, heroHeight],
          outputRange: [0, heroHeight],
          extrapolate: 'clamp',
        })
      : 0;
  // My Day's white content sheet (the group-label row that heads the list
  // content, below the Hero card) keeps SHEET_TOP_RADIUS while the Hero is
  // still on screen and flattens to 0 once the sheet docks against the tab
  // bar, where a rounded corner reads as a seam against the header instead of
  // a card edge over the Hero's grey backdrop. `heroTranslateY` clamps at
  // `heroHeight`, so that offset is exactly the dock point. The threshold
  // itself lives in `handleScroll`'s listener further down.
  //
  // Only My Day has a Hero to scroll past, so the other tabs stay at the
  // constant radius. This is deliberately NOT the behaviour TA-10495 #9
  // dropped: that one flattened as soon as the list scrolled at all.
  const sheetRadius = useRef(new Animated.Value(SHEET_TOP_RADIUS)).current;
  const sheetDockedRef = useRef(false);
  // Read from refs inside the scroll listener so `handleScroll` stays a stable
  // Animated.event across Hero re-measures and tab switches.
  const heroHeightRef = useRef(heroHeight);
  heroHeightRef.current = heroHeight;
  const sheetRadiusFollowsScrollRef = useRef(false);
  sheetRadiusFollowsScrollRef.current = deferredActiveTab === MyTasksTab.MyDay;

  const flatListRef = useRef<Animated.FlatList<MyTaskGroup>>(null);
  // Switching tabs reuses this same FlatList (only `data`/`renderItem`
  // change), so its native scroll position — and `scrollY`, which only
  // updates from real onScroll events — both carry over from whatever the
  // PREVIOUS tab was scrolled to. If My Day had been scrolled down before
  // switching away, `scrollY` stays at that stale offset; once the user
  // switches back, Hero remounts with `heroTranslateY` clamped against that
  // stale value instead of 0, so it paints translated away from its own
  // (correctly zero-offset) spacer — the spacer's reserved slot shows blank
  // while the real, already-loaded Hero content sits shifted underneath the
  // list content. A tap "fixes" it only because it happens to trigger a
  // fresh native onScroll event that resyncs `scrollY` to the true (0)
  // offset. Resetting both on every tab switch keeps `scrollY` truthful
  // instead of depending on an incidental future scroll event to correct it.
  useEffect(() => {
    scrollY.setValue(0);
    flatListRef.current?.scrollToOffset({ offset: 0, animated: false });
    // The sheet is undocked at offset 0, and this is also what leaves the
    // non-My-Day tabs (whose listener below no-ops) parked at the constant
    // default radius.
    sheetDockedRef.current = false;
    sheetRadius.setValue(SHEET_TOP_RADIUS);
  }, [activeTab, scrollY, sheetRadius]);

  // Unscheduled load-more: button-controlled (TA fix — the previous
  // scroll-position sentinel, tracked via manual onLayout/measure math
  // across the wrapper Box + MyTaskGroupCard's tasks list + the individual
  // sentinel row, added jank to every scroll frame on the Unscheduled card).
  // A press now drives the fetch directly instead of comparing live scroll
  // offset against a measured row position.
  const unscheduledHasMore =
    allTasksUnscheduledQuery.pageInfo?.hasNextPage ?? false;
  const [unscheduledLoadingMore, setUnscheduledLoadingMore] = useState(false);
  const handleUnscheduledLoadMore = useCallback(() => {
    if (!unscheduledHasMore || unscheduledLoadingMore) return;
    setUnscheduledLoadingMore(true);
    Promise.resolve(allTasksUnscheduledQuery.fetchFromCursorAsync()).finally(
      () => setUnscheduledLoadingMore(false)
    );
  }, [unscheduledHasMore, unscheduledLoadingMore, allTasksUnscheduledQuery]);
  const handleScroll = useMemo(
    () =>
      Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
        useNativeDriver: true,
        // A border radius can't ride the native driver (only transform and
        // opacity can), so the dock state is derived here rather than
        // interpolated off `scrollY`. Everything but the two threshold-crossing
        // frames costs a single float compare — the ref guard keeps this off
        // the per-frame `Animated.timing`/setState path that made the old
        // measure-based scroll listeners on this screen janky.
        listener: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
          if (!sheetRadiusFollowsScrollRef.current) return;
          const docked = isSheetDockedAtOffset(
            event.nativeEvent.contentOffset.y,
            heroHeightRef.current
          );
          if (docked === sheetDockedRef.current) return;
          sheetDockedRef.current = docked;
          Animated.timing(sheetRadius, {
            toValue: docked ? 0 : SHEET_TOP_RADIUS,
            duration: SHEET_RADIUS_DURATION_MS,
            useNativeDriver: false,
          }).start();
        },
      }),
    [scrollY, sheetRadius]
  );

  // Group infinite-scroll prefetch: unlike Unscheduled, groups here are real
  // FlatList data/renderItem items, so FlatList's own onViewableItemsChanged
  // tells us exactly when the row PROJECT_GROUPS_LOAD_MORE_OFFSET from the
  // end becomes visible — no manual onLayout/scroll math needed.
  // onViewableItemsChanged must never change identity after mount (RN
  // warns/breaks otherwise), so it's created once via useRef and reads
  // everything it needs from refs kept fresh below. Applies on every tab —
  // all three are server-grouped now. My Day merges two due_group-scoped
  // connections (Overdue + Current), so "has more"/"fetch more" span both.
  const isAnyServerGrouped =
    isMyDayServerGrouped || isSevenDaysServerGrouped || isAllTasksServerGrouped;

  let projectGroupsHasMore = false;
  // Raw (undeduped) loaded-group counts across whichever source(s) are
  // active, used only to detect "a new page just landed" and reset the
  // trigger guard below.
  let activeProjectGroupsRawLength = 0;
  if (isMyDayServerGrouped) {
    projectGroupsHasMore =
      (myDayOverdueProjectGroupsQuery.pageInfo?.hasNextPage ?? false) ||
      (myDayTodayProjectGroupsQuery.pageInfo?.hasNextPage ?? false);
    activeProjectGroupsRawLength =
      myDayOverdueProjectGroupsQuery.groups.length +
      myDayTodayProjectGroupsQuery.groups.length;
  } else if (isSevenDaysServerGrouped) {
    projectGroupsHasMore =
      weekProjectGroupsQuery.pageInfo?.hasNextPage ?? false;
    activeProjectGroupsRawLength = weekProjectGroupsQuery.groups.length;
  } else if (isAllTasksServerGrouped) {
    projectGroupsHasMore =
      allTasksProjectGroupsQuery.pageInfo?.hasNextPage ?? false;
    activeProjectGroupsRawLength = allTasksProjectGroupsQuery.groups.length;
  }

  const [projectGroupsLoadingMore, setProjectGroupsLoadingMore] =
    useState(false);
  const isAnyServerGroupedRef = useRef(isAnyServerGrouped);
  isAnyServerGroupedRef.current = isAnyServerGrouped;
  const projectGroupsHasMoreRef = useRef(projectGroupsHasMore);
  projectGroupsHasMoreRef.current = projectGroupsHasMore;
  const groupsLengthRef = useRef(groups.length);
  groupsLengthRef.current = groups.length;
  const fetchProjectGroupsMoreAsyncRef = useRef<() => Promise<unknown>>(() =>
    Promise.resolve()
  );
  fetchProjectGroupsMoreAsyncRef.current = () => {
    if (isMyDayServerGrouped) {
      const pending: Promise<unknown>[] = [];
      if (myDayOverdueProjectGroupsQuery.pageInfo?.hasNextPage) {
        pending.push(myDayOverdueProjectGroupsQuery.fetchFromCursorAsync());
      }
      if (myDayTodayProjectGroupsQuery.pageInfo?.hasNextPage) {
        pending.push(myDayTodayProjectGroupsQuery.fetchFromCursorAsync());
      }
      return Promise.all(pending);
    }
    if (isSevenDaysServerGrouped) {
      return Promise.resolve(weekProjectGroupsQuery.fetchFromCursorAsync());
    }
    if (isAllTasksServerGrouped) {
      return Promise.resolve(allTasksProjectGroupsQuery.fetchFromCursorAsync());
    }
    return Promise.resolve();
  };
  const projectGroupsTriggeredRef = useRef(false);

  // A freshly (re)loaded page invalidates the trigger guard so scrolling
  // further can fetch the page after that one too.
  useEffect(() => {
    projectGroupsTriggeredRef.current = false;
  }, [activeProjectGroupsRawLength]);

  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 1 }).current;
  const handleViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (!isAnyServerGroupedRef.current) return;
      if (!projectGroupsHasMoreRef.current) return;
      if (projectGroupsTriggeredRef.current) return;
      const sentinelIndex = Math.max(
        0,
        groupsLengthRef.current - PROJECT_GROUPS_LOAD_MORE_OFFSET
      );
      const sentinelVisible = viewableItems.some(
        (item) =>
          item.isViewable && item.index !== null && item.index >= sentinelIndex
      );
      if (!sentinelVisible) return;

      projectGroupsTriggeredRef.current = true;
      setProjectGroupsLoadingMore(true);
      Promise.resolve(fetchProjectGroupsMoreAsyncRef.current()).finally(() =>
        setProjectGroupsLoadingMore(false)
      );
    }
  ).current;

  const listHeader = (
    <>
      {showHero && (
        <>
          <Animated.View
            key={heroTasksKey}
            testID='my-tasks-hero-wrapper'
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              // Above the spacer/list content declared after it (TA-10187):
              // heroHeight only gets its real value once onLayout fires, so on
              // the frame Hero first (re)appears — e.g. reopening a completed
              // overdue task — the spacer below is still 0-height and the
              // white-backgrounded list content would otherwise paint over
              // this view, showing a blank block where the hero card is.
              zIndex: 1,

              backgroundColor: theme.colors.grey01,
              paddingTop: theme.spacing.xs,
              transform: [{ translateY: heroTranslateY }],
            }}
            onLayout={(e) => setHeroHeight(e.nativeEvent.layout.height)}>
            <HeroCarousel tasks={heroTasks} />
          </Animated.View>
          <Box testID='my-tasks-hero-spacer' height={heroHeight} />
        </>
      )}
      {unscheduledGroup && (
        <Box
          testID='my-tasks-unscheduled-wrapper'
          marginTop='m'
          backgroundColor='white'>
          <MyTaskGroupCard
            group={unscheduledGroup}
            groupBy={groupByOption}
            onToggleComplete={handleToggleCompleteTask}
            unscheduled
            hideProjectName={hideProjectName}
            hasMore={unscheduledHasMore}
            loadingMore={unscheduledLoadingMore}
            onLoadMore={handleUnscheduledLoadMore}
          />
        </Box>
      )}
      {groups.length > 0 && (
        // The white background and the (animated, see `sheetRadius`) rounded
        // top corners live on this Animated.View rather than the Box below:
        // Restyle can't take an Animated.Value for a border radius, and the
        // corners have to be on the same element as the fill or there is
        // nothing for them to cut into.
        <Animated.View
          testID='my-tasks-group-label-sheet'
          style={{
            backgroundColor: theme.colors.white,
            borderTopLeftRadius: sheetRadius,
            borderTopRightRadius: sheetRadius,
            // Sibling of the Hero wrapper (zIndex: 1 above) in the same
            // ListHeaderComponent parent — RN stacks explicitly-zIndexed
            // siblings by that value regardless of document order, so
            // without a matching zIndex this row painted BEHIND the hero
            // instead of on top of it like the rest of the list content.
            zIndex: 1,
          }}>
          <Box
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'
            paddingHorizontal='m'
            paddingTop='xs'
            paddingBottom='s'>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              mt='xs'
              flexShrink={1}>
              {groupSectionLabel(groupByOption)}
            </Text>
            <Text
              testID='my-tasks-group-count'
              variant='webBodySecondary'
              color='textSecondary'
              numberOfLines={1}
              flexShrink={0}>
              {groupCountLabel(
                groupByOption,
                groups.length,
                isAnyServerGrouped && projectGroupsHasMore
              ).replace(' ', '\u00a0')}
            </Text>
          </Box>
        </Animated.View>
      )}
    </>
  );

  const renderGroup = useCallback(
    ({ item }: { item: MyTaskGroup }) => (
      <Box backgroundColor='white'>
        <MyTaskGroupCard
          group={item}
          groupBy={groupByOption}
          onToggleComplete={handleToggleCompleteTask}
          hideProjectName={hideProjectName}
          hideProgressBar={deferredActiveTab === MyTasksTab.SevenDays}
          onLoadMoreTasks={
            fetchMoreGroupTasksForActiveTab
              ? () => fetchMoreGroupTasksForActiveTab(item, 'tasks')
              : undefined
          }
        />
      </Box>
    ),
    [
      groupByOption,
      handleToggleCompleteTask,
      hideProjectName,
      deferredActiveTab,
      fetchMoreGroupTasksForActiveTab,
    ]
  );

  return (
    <Box flex={1} backgroundColor='white'>
      <MyTasksTabBar
        activeTab={activeTab}
        onTabPress={setActiveTab}
        onFilterPress={handleFilterPress}
        showDot={
          hasActiveFilters ||
          groupByOption !== defaultGroupByOption ||
          orderByOption !== MyTasksOrderBy.OverdueToLow
        }
      />
      {showInitialSkeleton ? (
        // The first skeleton card sat flush against the tab bar above, so the
        // cold-load state read as one solid block; a 16px gap separates it
        // from the header the same way the loaded list's own content does.
        <Box testID='my-tasks-loading-skeleton-spacer' paddingTop='m'>
          <SkeletonList
            testID='my-tasks-loading-skeleton'
            row={renderMyTaskGroupCardSkeletonRow}
          />
        </Box>
      ) : (
        <Box flex={1} overflow='hidden'>
          <Animated.FlatList
            ref={flatListRef}
            testID='my-tasks-group-list'
            data={groups}
            keyExtractor={(item: MyTaskGroup) => item.key}
            renderItem={renderGroup}
            ListHeaderComponent={listHeader}
            ListEmptyComponent={
              unscheduledGroup ? null : (
                <EmptyStateNoSearch
                  showSmall
                  variant='noFilterResults'
                  showDescription={hasActiveFilters}
                />
              )
            }
            ListFooterComponent={
              isAnyServerGrouped && projectGroupsLoadingMore ? (
                <Box position='relative' height={40}>
                  <ActivityIndicatorLoading />
                </Box>
              ) : null
            }
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.5}
            onViewableItemsChanged={handleViewableItemsChanged}
            viewabilityConfig={viewabilityConfig}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
              />
            }
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: theme.spacing.xxl * 2 + insets.bottom,
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          />
        </Box>
      )}
      {!hideFab && (
        <FloatingActionButton
          label={fabLabel}
          disabled={fabDisabled}
          onPress={onCreateTaskPress}
          isInsetsBottom={fabRespectsBottomInset}
        />
      )}
    </Box>
  );
};
